import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Language, languageNames, englishTranslations, TranslationKey } from './translations';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
  isRTL: boolean;
  isTranslating: boolean;
  availableLanguages: Record<string, string>;
  tKey: (key: TranslationKey, fallback?: string) => string;h
  warnIfLooksLikeKey: (text: string, where: string) => void;
}

interface ThemeContextType {
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  isDark: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Detect system theme preference
const detectSystemTheme = (): boolean => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

// Cache key for translations
const TRANSLATION_CACHE_KEY = 'dipps_translations_cache';
const TRANSLATION_CACHE_VERSION = '2';

// Get cached translations
const getCachedTranslations = (lang: Language): Record<string, string> | null => {
  try {
    const cached = localStorage.getItem(`${TRANSLATION_CACHE_KEY}_${lang}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.version === TRANSLATION_CACHE_VERSION) {
        return parsed.translations;
      }
    }
  } catch (e) {
    console.warn('[i18n] Failed to read cache:', e);
  }
  return null;
};

// Save translations to cache
const setCachedTranslations = (lang: Language, translations: Record<string, string>) => {
  try {
    localStorage.setItem(`${TRANSLATION_CACHE_KEY}_${lang}`, JSON.stringify({
      version: TRANSLATION_CACHE_VERSION,
      translations,
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.warn('[i18n] Failed to write cache:', e);
  }
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Load saved language from localStorage or default to English
  const [lang, setLangState] = useState<Language>(() => {
    const saved = localStorage.getItem('dipps_language');
    const isValid = saved && Object.keys(languageNames).includes(saved);
    console.log(`[i18n] Initializing language: saved=${saved}, valid=${isValid}`);
    return isValid ? saved as Language : 'en';
  });
  
  // Current translations (English base + translated strings)
  const [translations, setTranslations] = useState<Record<string, string>>(englishTranslations);
  const [isTranslating, setIsTranslating] = useState(false);
  
  const [theme, setThemeState] = useState<'light' | 'dark' | 'system'>(() => {
    const saved = localStorage.getItem('dipps_theme');
    return (saved as 'light' | 'dark' | 'system') || 'system';
  });
  const [systemDark, setSystemDark] = useState(detectSystemTheme);

  // Calculate if dark mode should be active
  const isDark = theme === 'dark' || (theme === 'system' && systemDark);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemDark(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply dark mode class to document
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('dipps_theme', theme);
  }, [isDark, theme]);

  // Fetch translations when language changes — alive guard prevents stale setState
  useEffect(() => {
    let alive = true;
    console.log(`[i18n] Language changed to: ${lang}`);

    const load = async () => {
      // English is the base - no translation needed
      if (lang === 'en') {
        if (alive) setTranslations(englishTranslations);
        return;
      }

      // Check cache first
      const cached = getCachedTranslations(lang);
      if (cached) {
        console.log(`[i18n] Using cached translations for ${lang}`);
        if (alive) setTranslations(cached);
        return;
      }

      // Fetch from API
      if (alive) setIsTranslating(true);
      console.log(`[i18n] Fetching translations for ${lang}...`);
      try {
        const response = await fetch("/api/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            strings: englishTranslations,
            targetLanguage: lang,
          }),
        });

        console.log(`[i18n] API response status: ${response.status}`);

        if (response.ok) {
          const data = await response.json();
          console.log(`[i18n] API response data keys:`, Object.keys(data));
          if ('translations' in data && data.translations) {
            const translated = data.translations as Record<string, string>;
            if (alive) {
              setTranslations(translated);
              setCachedTranslations(lang, translated);
              console.log(`[i18n] Translations loaded for ${lang}, ${Object.keys(translated).length} keys`);
            }
          } else {
            console.error('[i18n] No translations in response:', data);
            if (alive) setTranslations(englishTranslations);
          }
        } else {
          const errorText = await response.text();
          console.error('[i18n] Translation API error:', response.status, errorText);
          if (alive) setTranslations(englishTranslations);
        }
      } catch (error) {
        console.error('[i18n] Translation fetch error:', error);
        if (alive) setTranslations(englishTranslations);
      } finally {
        if (alive) setIsTranslating(false);
      }
    };

    load();
    return () => { alive = false; };
  }, [lang]);
  
  const setLang = (newLang: Language) => {
    console.log(`[i18n] Setting language to: ${newLang}`);
    // Clear stale translation cache for this language to force fresh fetch
    try {
      localStorage.removeItem(`${TRANSLATION_CACHE_KEY}_${newLang}`);
    } catch (e) { /* ignore */ }
    setLangState(newLang);
    try {
      localStorage.setItem('dipps_language', newLang);
      console.log(`[i18n] Language saved to localStorage: ${newLang}`);
    } catch (e) {
      console.error('[i18n] Failed to save language to localStorage:', e);
    }
  };

  const setTheme = (newTheme: 'light' | 'dark' | 'system') => {
    setThemeState(newTheme);
    localStorage.setItem('dipps_theme', newTheme);
  };
  
  // Memoized translation function with optional fallback
  const t = useMemo(() => (key: string, fallback = ''): string => {
    // Look up in active language, then English
    const value = translations[key] ?? englishTranslations[key];
    if (value != null) return value;

    // Key is missing — NEVER show raw camelCase to users
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing translation key: "${key}"`);
    }
    // Return fallback or empty string so || fallbacks in JSX still work
    return fallback || '';
  }, [translations]);
  
  // RTL support for Arabic, Hebrew, Farsi, Urdu
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  const isRTL = rtlLanguages.includes(lang);

  // Memoized typed wrapper — use in new code for compile-time key safety
  const tKey = useMemo(() => (key: TranslationKey, fallback = ''): string => t(key, fallback), [t]);

  // DEV-only leak detector for labels/aria-labels
  const warnIfLooksLikeKey = (text: string, where: string) => {
    if (!import.meta.env.DEV) return;
    if (/[a-z][A-Z]/.test(text) || /_/.test(text)) {
      console.warn(`[ui-leak] "${text}" looks like an internal key (${where})`);
    }
  };
  
  // Apply RTL direction to document
  useEffect(() => {
    const root = document.documentElement;
    if (isRTL) {
      root.dir = 'rtl';
      root.classList.add('rtl');
    } else {
      root.dir = 'ltr';
      root.classList.remove('rtl');
    }
  }, [isRTL]);

  // Memoize provider value to prevent unnecessary re-renders
  const langValue = useMemo(() => ({
    lang,
    setLang,
    t,
    isRTL,
    isTranslating,
    availableLanguages: languageNames,
    tKey,
    warnIfLooksLikeKey,
  }), [lang, t, isRTL, isTranslating, tKey]);
  
  return (
    <LanguageContext.Provider value={langValue}>
      <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
        {children}
      </ThemeContext.Provider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within LanguageProvider');
  }
  return context;
};
